import type { QuickFilterScopeStruct } from "@amzn/ads-blink-client";
import { QuickFilterScopeType, type QuickFilter } from "@amzn/ads-blink-client";
import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import {
    Button,
    Checkbox,
    Flex,
    HelpTip,
    InlineAlert,
    Input,
    Modal,
    Text,
} from "@amzn/storm-ui";
import { trash } from "@amzn/storm-ui-icons";
import type { AsyncState } from "@react-hookz/web";
import { isEmpty } from "lodash";
import type { ReactElement } from "react";
import { useEffect, useState, useMemo } from "react";
import { useStore } from "zustand";

import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../../../constants/telemetry";
import {
    notificationStore,
    NotificationType,
} from "../../../../store/notification";
import { salesforceStore } from "../../../../store/salesforce";
import { unifiedFilterStore } from "../../../../store/unifiedFilters";
import type { PartialUnifiedfilterState } from "../../../../utils/filters.utils";
import { encodeUnifiedFilterStatePayload } from "../../../../utils/filters.utils";
import {
    getAdvertiserId,
    isAccountAgnosticId,
} from "../../../../utils/permission.utils";
import { logWithTakt } from "../../../../utils/telemetry.utils";
import { ModalContentContainer } from "../../../common/styled.components";

import { CreateQuickFilterActionAsync } from "./useCreateQuickFilterAction";
import { DeleteQuickFilterActionAsync } from "./useDeleteQuickFilterAction";
import { UpdateQuickFilterActionAsync } from "./useUpdateQuickFilterAction";

interface QuickFilterManagementModalProps {
    isModalOpen: boolean;
    setIsModalOpen: (open: boolean) => void;
    handleSubmit: () => void;
    quickFilter?: QuickFilter;
    quickFilters: QuickFilter[];
}

export const QuickFilterManagementModal: React.FC<
    QuickFilterManagementModalProps
> = ({
    isModalOpen,
    setIsModalOpen,
    handleSubmit,
    quickFilter,
    quickFilters,
}): ReactElement => {
    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    const action = quickFilter ? "Update" : "Save";

    const [saveState, saveAction] = CreateQuickFilterActionAsync();

    const [updateState, updateAction] = UpdateQuickFilterActionAsync();

    const [deleteState, deleteAction] = DeleteQuickFilterActionAsync();

    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);

    const [newName, setNameName] = useState("");
    const [isGlobal, setIsGlobal] = useState(false);

    const [
        isLastSearchParametersDifferent,
        applyFilters,
        setCurrentQuickFilterId,
    ] = useStore(unifiedFilterStore, (state) => [
        state.isLastSearchParametersDifferent,
        state.setIsContentDirty,
        state.setCurrentQuickFilterId,
    ]);

    const closeModal = () => {
        setIsModalOpen(false);
    };

    useEffect(() => {
        if (isModalOpen && quickFilter) {
            setNameName(quickFilter.name ?? "");
            setIsGlobal(quickFilter.scopeType === QuickFilterScopeType.GLOBAL);
        } else {
            setNameName("");
            setIsGlobal(false);
        }
    }, [isModalOpen]);

    const getNewScope = (): QuickFilterScopeStruct => {
        if (isGlobal) {
            return {
                scopeType: QuickFilterScopeType.GLOBAL,
                scopeValue: QuickFilterScopeType.GLOBAL,
            };
        } else {
            if (isAccountAgnosticId()) {
                return {
                    scopeType: QuickFilterScopeType.AGNOSTIC,
                    scopeValue: QuickFilterScopeType.AGNOSTIC,
                };
            } else {
                return {
                    scopeType: QuickFilterScopeType.SFID,
                    scopeValue: getAdvertiserId(),
                };
            }
        }
    };

    const validationError = useMemo(() => {
        // Testing required - should be able to create filters with the same name if different scope
        const trimmedName = newName.trim();
        const targetScopeType =
            quickFilter?.scopeType ?? getNewScope().scopeType;

        if (!trimmedName) {
            return "Name is required";
        }

        const filtersWithinScope = quickFilters
            .filter((f) => {
                return f.scopeType === targetScopeType;
            })
            .filter((f) =>
                quickFilter ? f.identifier !== quickFilter.identifier : true,
            )
            .map((f) => f.name?.toLowerCase());

        if (filtersWithinScope.includes(trimmedName.toLowerCase())) {
            return "A filter with this name already exists";
        }

        return null;
    }, [newName, quickFilters, quickFilter, getNewScope().scopeValue]);

    const saveQuickFilter = async () => {
        const newScope = getNewScope();

        const { searchQuery, filters } = unifiedFilterStore.getState();

        const payloadToEncode: PartialUnifiedfilterState = {
            filters,
            searchQuery,
        };

        const filter = encodeUnifiedFilterStatePayload(payloadToEncode, false);

        if (!filter) {
            upsertNotification(
                "Failed to parse current filter state when saving. Please try again, or contact support.",
                NotificationType.ERROR,
            );
            return;
        }

        setIsSubmitLoading(true);
        let state: AsyncState<any>;
        if (quickFilter) {
            state = updateState;
            await updateAction.execute(quickFilter!, {
                newScope,
                // newFilter: filter, // Filter content updates disabled per business requirements
                newName,
            });

            logWithTakt(TELEMETRY_ID.QUICK_FILTER.UPDATE, {
                id: quickFilter.identifier,
            });
            PhoenixMetrics.emit(
                TELEMETRY_ID.QUICK_FILTER.UPDATE,
                PHOENIX_FEATURE.QUICK_FILTERS,
                {
                    customData: {
                        id: quickFilter.identifier,
                    },
                },
            );
        } else {
            state = saveState;
            const newFilter = await saveAction.execute({
                ...newScope,
                filter,
                name: newName,
            });
            const newFilterId = newFilter?.data?.identifier;

            // Set as current filter immediately after successful save
            if (newFilterId) {
                setCurrentQuickFilterId(newFilterId);
            }

            logWithTakt(TELEMETRY_ID.QUICK_FILTER.CREATE, {
                id: newFilterId,
            });
            PhoenixMetrics.emit(
                TELEMETRY_ID.QUICK_FILTER.CREATE,
                PHOENIX_FEATURE.QUICK_FILTERS,
                {
                    customData: {
                        id: newFilterId,
                    },
                },
            );
        }
        setIsSubmitLoading(false);

        // Error states are handled in the actions themselves
        if (state.status !== "error") {
            upsertNotification(
                `Successfully ${action.toLocaleLowerCase()}d your quick filter: ${newName}`,
                NotificationType.SUCCESS,
            );
            handleSubmit();
            closeModal();

            // If the user saved this search without actually performing it, go ahead and perform it
            if (isLastSearchParametersDifferent()) {
                applyFilters(true);
            }
        }
    };

    const deleteQuickFilter = async () => {
        setIsDeleteLoading(true);
        await deleteAction.execute(quickFilter!);
        setIsDeleteLoading(false);

        logWithTakt(TELEMETRY_ID.QUICK_FILTER.DELETE, {
            id: quickFilter!.identifier,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.QUICK_FILTER.DELETE,
            PHOENIX_FEATURE.QUICK_FILTERS,
            {
                customData: {
                    id: quickFilter!.identifier,
                },
            },
        );

        // Error states are handled in the actions themselves
        if (deleteState.status !== "error") {
            upsertNotification(
                `Successfully deleted your quick filter: ${quickFilter!.name}`,
                NotificationType.SUCCESS,
            );
            handleSubmit();
            closeModal();
        }
    };

    const Footer = () => (
        <>
            {quickFilter && (
                <Button
                    data-testid="quickfilter-modal-delete-button"
                    iconStart={trash}
                    onClick={() => void deleteQuickFilter()}
                    loading={isDeleteLoading}
                >
                    Delete
                </Button>
            )}
            <Button
                data-testid="quickfilter-modal-submit-button"
                onClick={() => void saveQuickFilter()}
                disabled={!isEmpty(validationError)}
                variant="primary"
                loading={isSubmitLoading}
            >
                {action}
            </Button>
        </>
    );

    return (
        <Modal
            isOpen={isModalOpen}
            header={action + " Quick Filter"}
            footer={<Footer />}
            onClose={closeModal}
        >
            <ModalContentContainer>
                <Flex direction="column" rowGap="xsmall">
                    {quickFilter && (
                        <InlineAlert
                            data-testid="quick-filter-alert"
                            variant="info"
                            message="Changes will only update the fields below. To filter by different criteria, create a new filter"
                        />
                    )}
                    <Input
                        id={"quick-filter-name"}
                        fullWidth={true}
                        data-testid="quick-filter-name"
                        onChange={(event) => setNameName(event.target.value)}
                        placeholder="My Quick Filter"
                        label={"Name"}
                        value={newName}
                        statusType={validationError ? "error" : undefined}
                        message={() => (
                            <InlineAlert
                                variant={validationError ? "error" : undefined}
                                message={validationError}
                            />
                        )}
                    />

                    <Checkbox
                        onChange={(event) => setIsGlobal(event.target.checked)}
                        checked={isGlobal}
                        data-testid="quick-filter-scope"
                        label={
                            <Text>
                                Save across my Salesforce book of business
                                <HelpTip>
                                    Check this button to apply across your
                                    Salesforce book of business. Leave unchecked
                                    to apply only to the current Salesforce
                                    account:{" "}
                                    <strong>
                                        {salesforceStore.getState().sfContext
                                            .name ?? "Account Agnostic"}
                                    </strong>
                                </HelpTip>
                            </Text>
                        }
                    />
                </Flex>
            </ModalContentContainer>
        </Modal>
    );
};
